import { NavLink, useNavigate } from "react-router-dom";
import {
  Database,
  History,
  LayoutDashboard,
  ListChecks,
  LogOut,
  Send,
  SlidersHorizontal,
  Smartphone,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { S, T } from "@/lib/strings";
import { post } from "@/lib/api";
import { useFleetPresence } from "@/hooks/useFleetSummary";
import { useForgetIdentity, useWhoami } from "@/hooks/useWhoami";
import { ConnectionBadge } from "@/components/ConnectionBadge";
import { Tooltip } from "@/components/ui/Tooltip";
import { Skeleton } from "@/components/ui/Skeleton";

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
}

interface NavGroup {
  title: string;
  items: NavItem[];
}

/**
 * Three groups, in the order the work happens: watch the fleet, act on it,
 * configure it. A flat list of twenty links (the fork's shape) makes the
 * operator re-read the whole thing every time.
 */
export const NAV_GROUPS: NavGroup[] = [
  {
    title: S.navGroupMonitor,
    items: [
      { to: "/overview", label: S.navOverview, icon: LayoutDashboard },
      { to: "/devices", label: S.navDevices, icon: Smartphone },
      { to: "/runs", label: S.navRuns, icon: History },
    ],
  },
  {
    title: S.navGroupWork,
    items: [
      { to: "/tasks", label: S.navTasks, icon: ListChecks },
      { to: "/dispatch", label: S.navDispatch, icon: Send },
      { to: "/data", label: S.navData, icon: Database },
    ],
  },
  {
    title: S.navGroupSystem,
    items: [{ to: "/settings/fleet", label: S.navFleetSettings, icon: SlidersHorizontal }],
  },
];

export function Sidebar() {
  const navigate = useNavigate();
  const presence = useFleetPresence();
  const { username, role } = useWhoami();
  const forget = useForgetIdentity();

  async function signOut() {
    try {
      await post("/auth/logout");
    } catch {
      // A failed logout still clears the client: the cookie is HttpOnly, so the
      // worst case is a session that outlives the tab, and the server expires it.
    }
    forget();
    toast.success(S.loggedOut);
    navigate("/login", { replace: true });
  }

  return (
    <nav
      className={cn(
        "flex md:flex-col md:w-[220px] shrink-0",
        "bg-[var(--color-panel)] border-b md:border-b-0 md:border-r border-[var(--color-border-subtle)]",
        "p-2 md:p-3 overflow-x-auto md:overflow-x-visible",
      )}
    >
      <div className="hidden md:flex items-center gap-2.5 px-2 pt-2 pb-4">
        <div className="size-7 rounded-lg bg-gradient-to-br from-[#5e6ad2] to-[#7170ff] grid place-items-center text-[10px] font-semibold text-white">
          IC
        </div>
        <span className="text-sm font-semibold tracking-[-0.165px]">{S.appName}</span>
      </div>

      <div className="flex md:flex-col gap-1 md:gap-4">
        {NAV_GROUPS.map((group) => (
          <div key={group.title} className="flex md:flex-col md:gap-0.5 gap-1">
            <div className="hidden md:block px-2.5 pb-1 text-[10px] font-semibold uppercase tracking-wider text-[var(--color-fg-faint)]">
              {group.title}
            </div>
            {group.items.map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm font-medium tracking-[-0.165px] whitespace-nowrap transition-colors",
                    "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--color-accent)]",
                    isActive
                      ? "bg-[rgba(255,255,255,0.05)] text-[var(--color-fg)]"
                      : "text-[var(--color-fg-subtle)] hover:bg-[rgba(255,255,255,0.04)] hover:text-[var(--color-fg)]",
                  )
                }
              >
                <Icon size={14} className="opacity-80 shrink-0" />
                <span>{label}</span>
              </NavLink>
            ))}
          </div>
        ))}
      </div>

      <div className="hidden md:block flex-1" />

      <div className="hidden md:flex flex-col gap-2 px-1 pt-3">
        <PresenceBadge presence={presence} />

        <div className="flex items-center justify-between gap-2 px-1.5">
          <Tooltip content={role ? `role: ${role}` : undefined}>
            <span className="truncate text-[11px] text-[var(--color-fg-faint)]">
              {username ?? "—"}
            </span>
          </Tooltip>
          <ConnectionBadge />
        </div>

        <button
          type="button"
          onClick={signOut}
          className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-xs font-medium text-[var(--color-fg-subtle)] transition-colors hover:bg-[rgba(255,255,255,0.04)] hover:text-[var(--color-fg)] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--color-accent)]"
        >
          <LogOut size={13} />
          <span>{S.signOut}</span>
        </button>
      </div>
    </nav>
  );
}

function PresenceBadge({
  presence,
}: {
  presence: { total: number; online: number; stale: number; offline: number } | null;
}) {
  if (!presence) return <Skeleton className="h-[42px] w-full" />;

  const { online, total, stale, offline } = presence;
  const allWell = stale === 0 && offline === 0;

  return (
    <NavLink
      to="/devices"
      className="rounded-md border border-[var(--color-border-subtle)] bg-[rgba(255,255,255,0.02)] px-2.5 py-1.5 transition-colors hover:bg-[rgba(255,255,255,0.05)] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--color-accent)]"
    >
      <div className="flex items-baseline gap-1.5">
        <span
          className={cn(
            "size-1.5 rounded-full",
            allWell ? "bg-[#10b981]" : "bg-[#f59e0b]",
          )}
        />
        <span className="text-[11px] text-[var(--color-fg-subtle)]">在線</span>
        <span className="font-mono text-[13px] font-medium tabular-nums text-[var(--color-fg)]">
          {online}/{total}
        </span>
      </div>
      {!allWell && (
        <div className="mt-0.5 pl-3 font-mono text-[10px] text-[var(--color-fg-faint)]">
          {T.deviceHint(stale, offline)}
        </div>
      )}
    </NavLink>
  );
}
